import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { PrismEditor } from '@lender/prism-editor';
import { Box, Divider } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { getConditionalObject } from 'utils/data';

import {
  selectDuplicateDynamicFieldNames,
  selectDynamicFieldsMap,
  selectDynamicFieldsSchema,
} from 'store/forms/selectors';

import FormEditorHeader from './FormEditorHeader';
import { renderPrismWidgetEditor } from './PrismFormEditor.pickers';
import { PRISM_SIGNATURE_LABELS } from './PrismFormEditor.pickers.constants';
import { createDynamicFieldLabelResolver } from './PrismFormEditor.pickers.utils';
import { editorContainerSx, rootSx } from './PrismFormEditor.styles';

const defaultForm = {};

const PrismFormEditor = ({
  value = '',
  onChange = _.noop,
  onDocumentChange = _.noop,
  className = '',
  readOnly = false,
  header,
  formActions = null,
  form = defaultForm,
  isEditMode = false,
  isEditorHeaderAllowed = true,
  isLegacyTemplateHtml = false,
  initialDocumentJson = null,
  prismEditorRef = null,
  onExportPdf = null,
}) => {
  const dynamicFieldsSchema = useSelector(selectDynamicFieldsSchema);
  const dynamicFieldsMap = useSelector(selectDynamicFieldsMap);
  const duplicateDynamicFieldNames = useSelector(selectDuplicateDynamicFieldNames);

  const resolveDynamicFieldLabel = useMemo(
    () =>
      createDynamicFieldLabelResolver({
        dynamicFieldsSchema,
        dynamicFieldsMap,
        duplicateDynamicFieldNames,
      }),
    [dynamicFieldsSchema, dynamicFieldsMap, duplicateDynamicFieldNames],
  );

  const initialDocumentJsonRef = useRef(initialDocumentJson);
  const initialValueRef = useRef(value || '');

  const lastEmittedRef = useRef(initialValueRef.current);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onEditorChange = useCallback(html => {
    if (html === lastEmittedRef.current) {
      return;
    }

    lastEmittedRef.current = html;
    onChangeRef.current?.(html);
  }, []);

  return (
    <Box className={className} sx={rootSx}>
      {isEditorHeaderAllowed && (
        <FormEditorHeader
          form={form}
          formActions={formActions}
          header={header}
          isEditMode={isEditMode}
        />
      )}
      <Divider variant="fullWidth" />
      <Box className={readOnly ? 'read-only' : ''} sx={editorContainerSx}>
        <PrismEditor
          fallbackFontsBaseUrl={process.env.PRISM_FALLBACK_FONTS_URL}
          initialValue={initialValueRef.current}
          isLegacyTemplateHtml={isLegacyTemplateHtml}
          readOnly={readOnly}
          ref={prismEditorRef}
          renderWidgetEditor={renderPrismWidgetEditor}
          resolveDynamicFieldLabel={resolveDynamicFieldLabel}
          signatureLabels={PRISM_SIGNATURE_LABELS}
          variant="document"
          onChange={onEditorChange}
          onDocumentChange={onDocumentChange}
          onExportPDF={onExportPdf}
          {...getConditionalObject(!_.isNil(initialDocumentJsonRef.current), () => ({
            initialDocumentJson: initialDocumentJsonRef.current,
          }))}
        />
      </Box>
    </Box>
  );
};

PrismFormEditor.propTypes = {
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  form: PropTypes.object,
  value: PropTypes.string,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  isEditMode: PropTypes.bool,
  isEditorHeaderAllowed: PropTypes.bool,
  isLegacyTemplateHtml: PropTypes.bool,
  formActions: PropTypes.node,
  initialDocumentJson: PropTypes.object,
  prismEditorRef: PropTypes.shape({ current: PropTypes.object }),
  onChange: PropTypes.func,
  onDocumentChange: PropTypes.func,
  onExportPdf: PropTypes.func,
};

export default PrismFormEditor;
